import * as React from 'react'

import { observer } from 'mobx-react'

import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import {
  CompanyGroupingOption,
  CompanyListSelector,
  ICompanyMetadata,
} from '~/client/src/shared/components/CompanyListSelector'
import { DeleteIcon } from '~/client/src/shared/components/icons/DeleteIcon'
import { DuplicateIcon } from '~/client/src/shared/components/icons/DuplicateIcon'
import { MoreIcon } from '~/client/src/shared/components/icons/MoreIcon'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import type { IListSelectorSearchConfig } from '~/client/src/shared/components/ListSelector/types'
import { PopoverTagPicker } from '~/client/src/shared/components/PopoverTagPicker/PopoverTagPicker'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/client/src/shared/components/shadcn-ui/select'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import {
  AndOrOperator,
  getOperatorDisplayText,
  IsNotOperator,
} from '~/client/src/shared/models/LogicOperation'
import OperationRule from '~/client/src/shared/models/OperationRule'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import getCompanyTypeTranslate from '~/client/src/shared/utils/getCompanyTypeTranslate'

import { FilterCategory, IFilterGroup } from './signUpNotificationRuleUtils'
import { ISignUpNotificationRulesApi } from './useSignUpNotificationRules'

interface IProps {
  rule: OperationRule
  store: ISignUpNotificationRulesApi
  companiesStore: CompaniesStore
  filterIndex: number
  filter: IFilterGroup
  isFirstRow: boolean
  connector?: AndOrOperator
  selectorCompanies: Company[]
  companyMetadata: Map<string, ICompanyMetadata>
}

const FilterRow: React.FC<IProps> = observer(
  ({
    rule,
    store,
    companiesStore,
    filterIndex,
    filter,
    isFirstRow,
    connector,
    selectorCompanies,
    companyMetadata,
  }) => {
    const [showCompanyPicker, setShowCompanyPicker] = React.useState(false)
    const [showCompanyTypePicker, setShowCompanyTypePicker] =
      React.useState(false)

    const selectedCompanyIdsSet = React.useMemo(
      () => new Set(filter.valueIds),
      [filter.valueIds],
    )

    const handleCompanyApply = React.useCallback(
      (newIds: Set<string>) => {
        store.setFilterValues(rule, filterIndex, [...newIds])
        setShowCompanyPicker(false)
      },
      [rule, filterIndex],
    )

    const handleCategorySelect = React.useCallback(
      (value: string) => {
        if (value !== filter.category) {
          store.setFilterCategory(rule, filterIndex, value as FilterCategory)
        }
      },
      [rule, filterIndex, filter.category],
    )

    const handleIsNotChange = React.useCallback(
      (value: string) => {
        store.setFilterIsNot(rule, filterIndex, value === IsNotOperator.IS_NOT)
      },
      [rule, filterIndex],
    )

    // Company value display
    const selectedCompanies = React.useMemo(
      () =>
        filter.category === 'company'
          ? filter.valueIds
              .map(id => {
                const company = companiesStore.getCompanyById(id)
                return company
                  ? { id, name: company.name, avatarUrl: company.avatarUrl }
                  : null
              })
              .filter(Boolean)
          : [],
      [filter.category, filter.valueIds, companiesStore],
    )

    const handleRemoveCompany = React.useCallback(
      (companyId: string) => {
        store.setFilterValues(
          rule,
          filterIndex,
          filter.valueIds.filter(id => id !== companyId),
        )
      },
      [rule, filterIndex, filter.valueIds],
    )

    // CompanyType items for ListSelector
    const allCompanyTypeTags = companiesStore.allCompanyTypeTags
    const activeCompanies = companiesStore.availableSortedCompanies
    const companyTypeListItems = React.useMemo(() => {
      const activeTypeTagIds = new Set(activeCompanies.flatMap(c => c.typeTags))
      return allCompanyTypeTags
        .filter(tag => activeTypeTagIds.has(tag.id))
        .map(tag => ({
          id: tag.id,
          label: getCompanyTypeTranslate(tag.value),
        }))
    }, [allCompanyTypeTags, activeCompanies])

    const companyTypeSelectedIds = React.useMemo(
      () => new Set(filter.valueIds),
      [filter.valueIds],
    )

    const companyTypeSearchConfig = React.useMemo(
      (): IListSelectorSearchConfig<{ id: string; label: string }> => ({
        searchFn: (item, query) => item.label.toLowerCase().includes(query),
      }),
      [],
    )

    const handleCompanyTypeApply = React.useCallback(
      (newIds: Set<string>) => {
        store.setFilterValues(rule, filterIndex, [...newIds])
        setShowCompanyTypePicker(false)
      },
      [rule, filterIndex],
    )

    const selectedCompanyTypes = React.useMemo(
      () =>
        filter.category === 'companyType'
          ? filter.valueIds
              .map(id => {
                const tag = allCompanyTypeTags.find(t => t.id === id)
                return tag
                  ? { id, name: getCompanyTypeTranslate(tag.value) }
                  : null
              })
              .filter(Boolean)
          : [],
      [filter.category, filter.valueIds, allCompanyTypeTags],
    )

    const handleRemoveCompanyType = React.useCallback(
      (typeId: string) => {
        store.setFilterValues(
          rule,
          filterIndex,
          filter.valueIds.filter(id => id !== typeId),
        )
      },
      [rule, filterIndex, filter.valueIds],
    )

    const connectorItems = [
      {
        value: AndOrOperator.AND,
        label: getOperatorDisplayText(AndOrOperator.AND),
      },
      {
        value: AndOrOperator.OR,
        label: getOperatorDisplayText(AndOrOperator.OR),
      },
    ]
    const categoryItems = [
      { value: 'company', label: Localization.translator.company },
      { value: 'companyType', label: Localization.translator.companyType },
    ]
    const operatorItems = [
      {
        value: IsNotOperator.IS,
        label: getOperatorDisplayText(IsNotOperator.IS),
      },
      {
        value: IsNotOperator.IS_NOT,
        label: getOperatorDisplayText(IsNotOperator.IS_NOT),
      },
    ]

    return (
      <div className="tw:flex tw:items-start tw:gap-2 tw:w-full">
        {/* Column 1: "Only when:" label or AND/OR connector — 74px */}
        {isFirstRow ? (
          <span className="tw:text-body-md tw:text-muted-foreground tw:text-right tw:w-[74px] tw:shrink-0 tw:h-8 tw:flex tw:items-center tw:justify-end">
            {Localization.translator.onlyWhen}
          </span>
        ) : (
          <div className="tw:w-[74px] tw:shrink-0">
            <Select
              value={connector}
              onValueChange={value =>
                store.setConnector(
                  rule,
                  filterIndex - 1,
                  value as AndOrOperator,
                )
              }
              items={connectorItems}
            >
              <SelectTrigger className="tw:w-full tw:min-w-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {connectorItems.map(item => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Column 2: Category — flex-1 */}
        <div className="tw:flex-1 tw:min-w-0">
          <Select
            value={filter.category}
            onValueChange={handleCategorySelect}
            items={categoryItems}
          >
            <SelectTrigger className="tw:w-full tw:min-w-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categoryItems.map(item => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Column 3: IS / IS NOT — 96px */}
        <div className="tw:w-[96px] tw:shrink-0">
          <Select
            value={filter.isNot ? IsNotOperator.IS_NOT : IsNotOperator.IS}
            onValueChange={handleIsNotChange}
            items={operatorItems}
          >
            <SelectTrigger className="tw:w-full tw:min-w-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {operatorItems.map(item => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Column 4: Value picker — 400px */}
        <div className="tw:flex tw:gap-2 tw:items-center tw:w-[400px] tw:shrink-0">
          {filter.category === 'company' ? (
            <div className="tw:flex-1 tw:min-w-0">
              <PopoverTagPicker
                isOpen={showCompanyPicker}
                onOpen={() => setShowCompanyPicker(true)}
                onClose={() => setShowCompanyPicker(false)}
                placeholder={Localization.translator.selectCompany}
                triggerLabel={Localization.translator.selectCompany}
                popoverClassName="tw:w-[400px] tw:max-h-[800px] tw:min-h-[600px]"
                popoverContent={
                  <CompanyListSelector
                    selectedCompanyIds={selectedCompanyIdsSet}
                    companies={selectorCompanies}
                    companyMetadata={companyMetadata}
                    defaultGrouping={CompanyGroupingOption.TYPE}
                    title={Localization.translator.selectCompany}
                    showActions
                    onClose={() => setShowCompanyPicker(false)}
                    onApply={handleCompanyApply}
                    className="tw:flex-1 tw:min-h-0"
                  />
                }
              >
                {selectedCompanies.map(company => (
                  <Tag
                    key={company.id}
                    avatar={
                      <Avatar
                        src={company.avatarUrl}
                        size={AvatarSize.XMinuscule}
                        avatarType={AvatarType.Company}
                      />
                    }
                    onDismiss={() => handleRemoveCompany(company.id)}
                  >
                    {company.name}
                  </Tag>
                ))}
              </PopoverTagPicker>
            </div>
          ) : (
            <div className="tw:flex-1 tw:min-w-0">
              <PopoverTagPicker
                isOpen={showCompanyTypePicker}
                onOpen={() => setShowCompanyTypePicker(true)}
                onClose={() => setShowCompanyTypePicker(false)}
                placeholder={Localization.translator.selectCompanyType}
                triggerLabel={Localization.translator.selectCompanyType}
                popoverClassName="tw:w-[400px] tw:max-h-[800px] tw:min-h-[600px]"
                popoverContent={
                  <ListSelector.Root<{ id: string; label: string }>
                    items={companyTypeListItems}
                    selectedIds={companyTypeSelectedIds}
                    draft
                    search={companyTypeSearchConfig}
                    className="tw:flex-1 tw:min-h-0"
                  >
                    <ListSelector.Title
                      title={Localization.translator.selectCompanyType}
                      onClose={() => setShowCompanyTypePicker(false)}
                    />
                    <ListSelector.Toolbar />
                    <ListSelector.Counter
                      formatTotal={Localization.translator.xTypesCount}
                    />
                    <ListSelector.List
                      renderItemContent={(item: {
                        id: string
                        label: string
                      }) => <span className="tw:truncate">{item.label}</span>}
                    />
                    <ListSelector.Actions
                      onApply={handleCompanyTypeApply}
                      onClose={() => setShowCompanyTypePicker(false)}
                    />
                  </ListSelector.Root>
                }
              >
                {selectedCompanyTypes.map(type => (
                  <Tag
                    key={type.id}
                    onDismiss={() => handleRemoveCompanyType(type.id)}
                  >
                    {type.name}
                  </Tag>
                ))}
              </PopoverTagPicker>
            </div>
          )}
        </div>
        {/* More menu — 28px */}
        <DropdownMenu>
          <DropdownMenuTrigger className="tw:inline-flex tw:size-7 tw:items-center tw:justify-center tw:rounded tw:border-0 tw:bg-transparent tw:p-0 tw:text-foreground tw:outline-none tw:transition-colors tw:hover:bg-muted tw:focus-visible:border-ring tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50">
            <MoreIcon size="sm" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="bottom"
            align="end"
            className="tw:w-[150px]"
          >
            <DropdownMenuItem
              onClick={() => store.duplicateFilter(rule, filterIndex)}
            >
              <DuplicateIcon />
              <span>{Localization.translator.duplicate}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() => store.removeFilter(rule, filterIndex)}
            >
              <DeleteIcon />
              <span>{Localization.translator.delete}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  },
)

export default FilterRow
